/**
 * Shared helpers for command runners (docs/architecture/command-parity-plan.md).
 *
 * Runners turn canonical commands into: either an immediate document change
 * (through entityOps undoable helpers) or an InteractionSpec driving the
 * generic command engine. Every completion returns a command-line message.
 */

import * as THREE from 'three';
import { createElement } from 'react';
import { Line } from '@react-three/drei';
import { useAppStore, type Entity } from '../../../state/store';
import { useCommandInteract, type InteractionSpec } from '../commandInteract';
import { makeEntity, undoableAdd, undoableDelete, polylineEntity } from '../entityOps';
export { makeEntity };
import type { Pt3 } from '../geometryMath';
import { expandBulgePolyline } from '../geometryMath';
import type { MeshPayload } from '../meshFactory';
import { notify } from '../../../utils/notify';
import type { NurbsCurveData, NurbsSurfaceData, NurbsPolysurfData } from '../../geometry/nurbs';
import { sampleCurve, tessellateSurface, interpolateCurve } from '../../geometry/nurbs';
import { makeSplineData } from '../splineMath';
import { recordHistory } from '../../geometry/history';
import { refreshKernelTessellation as refreshKernelTessellationData } from '../../utils/dataTransform';
import { UX_COLORS } from '../../../themes/colors';

/** Selected CAD entity ids (warns when empty). */
export function selectedEntities(): Entity[] {
    const { entities, selectedIds } = useAppStore.getState();
    return selectedIds.map((id) => entities[id]).filter(Boolean) as Entity[];
}

export function requireSelection(label: string): Entity[] | null {
    const list = selectedEntities();
    if (list.length === 0) {
        notify(`${label}: select objects first.`, 'warning');
        return null;
    }
    return list;
}

/** Entity data bags are heterogeneous; read fields defensively. */
export function dataNum(e: Entity, key: string, fallback = 0): number {
    const v = (e.data as Record<string, unknown>)?.[key];
    return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}
export function dataArr(e: Entity, key: string): number[] {
    const v = (e.data as Record<string, unknown>)?.[key];
    return Array.isArray(v) ? (v as number[]) : [];
}
export function dataArrs(e: Entity, key: string): number[][] {
    const v = (e.data as Record<string, unknown>)?.[key];
    return Array.isArray(v) ? (v as number[][]) : [];
}

/** Convert any known drafting entity into a world-space point list. */
export function entityToPolyline(e: Entity): Pt3[] | null {
    const d = e.data as Record<string, any>;
    switch (e.type) {
        case 'line':
            return d.start && d.end ? [d.start, d.end] : null;
        case 'polyline':
        case 'polygon': {
            const raw = Array.isArray(d.vertices) && d.vertices.length >= 2 ? (d.vertices as Pt3[]) : null;
            if (!raw) return null;
            // Arc (bulge) segments expand to true arc geometry.
            const bulges = Array.isArray(d.bulges) ? (d.bulges as number[]) : undefined;
            return bulges && bulges.length > 0
                ? expandBulgePolyline(raw, bulges, !!d.closed)
                : raw;
        }
        case 'rectangle': {
            if (!Array.isArray(d.corner1) || !Array.isArray(d.corner2)) return null;
            const [x1, y1] = d.corner1, [x2, y2] = d.corner2;
            const z = d.corner1[2] ?? 0;
            return [[x1, y1, z], [x2, y1, z], [x2, y2, z], [x1, y2, z], [x1, y1, z]];
        }
        case 'circle': {
            if (!Array.isArray(d.center)) return null;
            const pts: Pt3[] = [];
            const seg = 64;
            for (let i = 0; i <= seg; i++) {
                const a = (i / seg) * Math.PI * 2;
                pts.push([d.center[0] + Math.cos(a) * d.radius, d.center[1] + Math.sin(a) * d.radius, d.center[2] ?? 0]);
            }
            return pts;
        }
        case 'arc': {
            if (!Array.isArray(d.center)) return null;
            let sweep = d.endAngle - d.startAngle;
            while (sweep <= 0) sweep += Math.PI * 2;
            const seg = Math.max(8, Math.round((sweep / (Math.PI * 2)) * 48));
            const pts: Pt3[] = [];
            for (let i = 0; i <= seg; i++) {
                const a = d.startAngle + (sweep * i) / seg;
                pts.push([d.center[0] + Math.cos(a) * d.radius, d.center[1] + Math.sin(a) * d.radius, d.center[2] ?? 0]);
            }
            return pts;
        }
        case 'ellipse': {
            if (!Array.isArray(d.center)) return null;
            const pts: Pt3[] = [];
            const seg = 48;
            const rot = d.rotation ?? 0;
            for (let i = 0; i <= seg; i++) {
                const t = (i / seg) * Math.PI * 2;
                const x = (d.radiusA ?? 1) * Math.cos(t), y = (d.radiusB ?? 1) * Math.sin(t);
                pts.push([
                    d.center[0] + x * Math.cos(rot) - y * Math.sin(rot),
                    d.center[1] + x * Math.sin(rot) + y * Math.cos(rot),
                    d.center[2] ?? 0,
                ]);
            }
            return pts;
        }
        case 'curve':
            return Array.isArray(d.controlPoints) && d.controlPoints.length >= 2 ? d.controlPoints : null;
        case 'hatch':
            return Array.isArray(d.boundary) && d.boundary.length >= 2 ? (d.boundary as Pt3[]) : null;
        case 'leader':
            return Array.isArray(d.tip) && Array.isArray(d.landing) ? [d.tip, d.landing] as Pt3[] : null;
        case 'image': {
            // PDF/raster underlay (Faz 9.7): the resolvable geometry IS the
            // placement frame — TRIM/OFFSET/DXF export treat the frame quad
            // as the outline (raster pixels never participate in curves).
            const pos = Array.isArray(d.position) ? d.position : [0, 0, 0];
            const w = Number(d.width) || 1;
            const h = Number(d.height) || 1;
            const z = pos[2] ?? 0;
            return [
                [pos[0], pos[1], z],
                [pos[0] + w, pos[1], z],
                [pos[0] + w, pos[1] + h, z],
                [pos[0], pos[1] + h, z],
                [pos[0], pos[1], z],
            ] as Pt3[];
        }
        case 'nurbs-curve':
            return Array.isArray(d.tessellation) && d.tessellation.length >= 2 ? (d.tessellation as Pt3[]) : null;
        case 'spline':
            // Faz 7.2 spline entity: the tessellation cache IS the resolved
            // geometry (rebuilt from fit/CV data on every mutation), so
            // TRIM/OFFSET/hatch bounds/dim-assoc measure the real curve.
            return Array.isArray(d.tessellation) && d.tessellation.length >= 2 ? (d.tessellation as Pt3[]) : null;
        case 'solid': {
            // Reconstruct the naked boundary (edges used exactly once) and
            // walk it into an ordered loop for reprofiling operations.
            const verts = dataArrs(e, 'vertices');
            const idx = dataArrs(e, 'indices');
            if (verts.length === 0 || idx.length === 0) return null;
            const edgeUse = new Map<string, { count: number; x: number; y: number }>();
            idx.forEach(([a, b, c]) => {
                ([[a, b], [b, c], [c, a]] as [number, number][]).forEach(([x, y]) => {
                    const key = x < y ? `${x}:${y}` : `${y}:${x}`;
                    const hit = edgeUse.get(key);
                    if (hit) hit.count += 1;
                    else edgeUse.set(key, { count: 1, x, y });
                });
            });
            const adj = new Map<number, number[]>();
            edgeUse.forEach((v) => {
                if (v.count !== 1) return;
                adj.set(v.x, [...(adj.get(v.x) ?? []), v.y]);
                adj.set(v.y, [...(adj.get(v.y) ?? []), v.x]);
            });
            const start = adj.keys().next().value as number | undefined;
            if (start === undefined) return null;
            const loop: Pt3[] = [verts[start] as Pt3];
            let prev = -1, cur = start;
            for (let guard = 0; guard < verts.length + 1; guard++) {
                const nexts = (adj.get(cur) ?? []).filter((n) => n !== prev);
                if (nexts.length === 0) break;
                const nxt = nexts[0];
                if (nxt === start) break;
                loop.push(verts[nxt] as Pt3);
                prev = cur;
                cur = nxt;
            }
            return loop.length >= 3 ? loop : null;
        }
        default:
            return null;
    }
}

/** Solid/mesh payload entity (single render branch for all 3D results). */
export function solidEntity(
    mesh: MeshPayload,
    name: string,
    opts: { color?: string; wire?: boolean; closed?: boolean; kind?: string; position?: Pt3 } = {}
): Entity {
    return makeEntity('solid', name, {
        vertices: mesh.vertices,
        indices: mesh.indices,
        color: opts.color ?? '#8fb4d9',
        wire: !!opts.wire,
        solidClosed: opts.closed ?? true,
        solidKind: opts.kind ?? 'solid',
        position: opts.position ?? [0, 0, 0],
    });
}

/** Read the stored mesh payload of a solid entity (null for non-solids). */
export function solidMesh(e: Entity): MeshPayload | null {
    if (e.type !== 'solid') return null;
    const vertices = e.data.vertices as number[][] | undefined;
    const indices = e.data.indices as number[][] | undefined;
    if (!Array.isArray(vertices) || !Array.isArray(indices)) return null;
    return { vertices, indices };
}

// ---------------- NURBS kernel citizens (R1) ----------------

/** Kernel curve entity: the data IS the control net + knots; the display
 *  tessellation is a cache, regenerated from the kernel when missing. */
export function nurbsCurveEntity(curve: NurbsCurveData, name: string, color = '#00ff00'): Entity {
    return makeEntity('nurbs-curve', name, {
        curve,
        color,
        tessellation: sampleCurve(curve, 64),
    });
}

/** Kernel surface entity with its tessellation cache. */
export function nurbsSurfaceEntity(surface: NurbsSurfaceData, name: string, opts: { color?: string; closed?: boolean } = {}): Entity {
    return makeEntity('nurbs-surface', name, {
        surface,
        color: opts.color ?? '#66c2a5',
        solidClosed: opts.closed ?? false,
        tessellation: tessellateSurface(surface, 24),
    });
}

/** Spline drafting entity (Faz 7.2): fit or CV source + tessellation cache. */
export function splineEntity(
    data: { fitPoints?: Pt3[]; controlPoints?: Pt3[] },
    name: string,
    opts: { degree?: number; knotParameterization?: 0 | 1 | 2; closed?: boolean; color?: string } = {}
): Entity {
    const bag = makeSplineData(data, opts);
    return makeEntity('spline', name, { ...bag, color: opts.color ?? '#00ff00' });
}

/** Regenerate the tessellation cache of a kernel entity from its kernel
 *  data (called after any kernel mutation so render stays honest). */
export function refreshKernelTessellation(data: Record<string, any>): Record<string, any> {
    return refreshKernelTessellationData(data);
}

/** True for kernel-citizen entities (curve or surface payloads). */
export function isKernelEntity(e: Entity | undefined): boolean {
    return !!e && (e.type === 'nurbs-curve' || e.type === 'nurbs-surface');
}

/** Read the kernel curve of an entity: true NURBS when present, otherwise
 *  an interpolating curve fitted through its polyline/line points (this is
 *  how the legacy `curve`/`polyline` citizens stay kernel-addressable). */
export function kernelCurveOf(e: Entity | undefined): NurbsCurveData | null {
    if (!e) return null;
    if (e.type === 'nurbs-curve' && e.data.curve) return e.data.curve as NurbsCurveData;
    const poly = entityToPolyline(e);
    if (!poly || poly.length < 2) return null;
    return interpolateCurve(poly.map((p) => [p[0], p[1], p[2] ?? 0] as Pt3));
}

/** Kernel polysurface entity: a face list sharing one display cache. */
export function nurbsPolysurfEntity(faces: NurbsPolysurfData['faces'], name: string, color = '#66c2a5'): Entity {
    const tess = faces.map((f) => tessellateSurface(f, 24));
    const vertices: number[][] = [];
    const indices: number[][] = [];
    tess.forEach((t) => {
        const off = vertices.length;
        vertices.push(...t.vertices);
        t.indices.forEach((tri) => indices.push(tri.map((k) => k + off)));
    });
    return makeEntity('nurbs-polysurf', name, {
        faces,
        color,
        solidClosed: false,
        tessellation: { vertices, indices },
    });
}

/** Read the kernel surface list of any surface-carrying entity. */
export function kernelSurfacesOf(e: Entity | undefined): NurbsSurfaceData[] {
    if (!e) return [];
    if (e.type === 'nurbs-surface' && e.data.surface) return [e.data.surface as NurbsSurfaceData];
    if (e.type === 'nurbs-polysurf' && Array.isArray(e.data.faces)) return e.data.faces as NurbsSurfaceData[];
    return [];
}

/** Mesh payload from any 3D entity: raw solid payload OR kernel cache. */
export function anyMeshOf(e: Entity | undefined): MeshPayload | null {
    if (!e) return null;
    const raw = solidMesh(e);
    if (raw) return raw;
    if (e.type === 'nurbs-surface' || e.type === 'nurbs-polysurf') {
        const t = e.data.tessellation as MeshPayload | undefined;
        if (t && Array.isArray(t.vertices) && Array.isArray(t.indices)) return t;
    }
    return null;
}

/** True when the entity carries kernel surface geometry. */
export function hasKernelSurface(e: Entity | undefined): boolean {
    return e?.type === 'nurbs-surface' || e?.type === 'nurbs-polysurf';
}

/** Read the kernel surface of an entity (null for non-surfaces). */
export function kernelSurfaceOf(e: Entity | undefined): NurbsSurfaceData | null {
    if (!e) return null;
    if (e.type === 'nurbs-surface' && e.data.surface) return e.data.surface as NurbsSurfaceData;
    return null;
}

/** Translate a raw data bag's point fields in place and return it. */
export function translateBag(data: Record<string, any>, d: [number, number, number]): Record<string, any> {
    const shift = (p: number[]): number[] => [p[0] + d[0], p[1] + d[1], (p[2] ?? 0) + d[2]];
    ['start', 'end', 'position', 'center'].forEach((k) => { if (Array.isArray(data[k])) data[k] = shift(data[k] as number[]); });
    ['vertices', 'controlPoints'].forEach((k) => { if (Array.isArray(data[k])) data[k] = (data[k] as number[][]).map(shift); });
    // kernel citizens: shift the parametric source AND the display cache
    if (data.curve && Array.isArray(data.curve.points)) {
        data.curve = { ...data.curve, points: (data.curve.points as number[][]).map(shift) };
        if (Array.isArray(data.tessellation)) data.tessellation = (data.tessellation as number[][]).map(shift);
    }
    if (data.surface && Array.isArray(data.surface.cv)) {
        data.surface = {
            ...data.surface,
            cv: (data.surface.cv as number[][][]).map((row) => row.map(shift)),
        };
        const t = data.tessellation as { vertices: number[][] } | undefined;
        if (t && Array.isArray(t.vertices)) t.vertices = t.vertices.map(shift);
    }
    return data;
}

/** 2D polyline entity from world points. */
export function curveEntity(points: Pt3[], name: string, opts: { closed?: boolean; color?: string } = {}) {
    return polylineEntity(points.map((p) => [p[0], p[1], p[2] ?? 0]), {
        name,
        closed: opts.closed,
        color: opts.color ?? '#ffffff',
    });
}

/** Start an interaction through the engine (all shells feed the prompts). */
export function runInteraction(spec: InteractionSpec): void {
    useCommandInteract.getState().start(spec);
}

/** Resolve picked entity ids to live entities (drops deleted ids). */
export function resolveInputs(ids: string[]): Entity[] {
    const { entities } = useAppStore.getState();
    return ids.map((i) => entities[i]).filter(Boolean) as Entity[];
}

/**
 * Create entities derived from picked sources as ONE undoable step and
 * register provenance for the HISTORY engine (R6-1): `make` is a pure
 * function sources → new entities; when any source mutates while Record
 * History is on, the engine reruns `make` and updates the same output ids.
 * Returns the created entities, or null when `make` cannot produce them.
 */
export function deriveEntities(
    actionId: string,
    inputs: string[],
    make: (sources: Entity[]) => Entity[] | null
): Entity[] | null {
    const out = make(resolveInputs(inputs));
    if (!out || out.length === 0) return null;
    const ids = undoableAdd(out);
    recordHistory(actionId, inputs, ids, (sources) => {
        const rebuilt = make(sources);
        return rebuilt
            ? rebuilt.map((e, i) => ({ ...e, id: ids[i] ?? e.id }))
            : null;
    });
    return out;
}

/** Standard steps. */
export const PT = (prompt: string) => ({ prompt, kind: 'point' as const });
export const PLoop = (prompt: string, minPoints: number) => ({ prompt, kind: 'point' as const, loop: true, minPoints });
export const PN = (prompt: string, def = 1) => ({ prompt, kind: 'numeric' as const, default: def });
export const PTxt = (prompt: string) => ({ prompt, kind: 'text' as const });
export const PK = (prompt: string, acceptTypes?: string[]) => ({ prompt, kind: 'pick' as const, acceptTypes });
export const PLoopK = (prompt: string, minPicks = 1, acceptTypes?: string[]) =>
    ({ prompt, kind: 'pick' as const, loop: true, minPicks, acceptTypes });

/** Rubber band preview: dashed polyline through points to the cursor. */
export function rubberBand(color: string = UX_COLORS.rubberBand) {
    return (points: THREE.Vector3[], cursor: THREE.Vector3 | null) => {
        const pts: [number, number, number][] = points.map((p) => [p.x, p.y, p.z]);
        if (cursor) pts.push([cursor.x, cursor.y, cursor.z]);
        if (pts.length < 2) return null;
        return createElement(Line, {
            points: pts,
            color,
            lineWidth: 1.5,
            dashed: true,
            dashSize: 0.2,
            gapSize: 0.1,
        });
    };
}

/** Delete the picked entity ids. */
export function deletePicked(ids: string[]): string {
    undoableDelete(ids);
    return `${ids.length} object(s) deleted.`;
}

/** Read a step value with fallback. */
export function valueAt(values: Record<number, number | string>, index: number, fallback: number): number {
    const v = values[index];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string') {
        const n = Number(v);
        if (Number.isFinite(n)) return n;
    }
    return fallback;
}
